import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL, api, apiFormData, getAuthHeaders } from '../utils/api';
import { Course } from '../types';

export const useCourses = () => {
  return useQuery({
    queryKey: ['courses'],
    queryFn: async (): Promise<Course[]> => {
      return await api('/courses');
    },
  });
};

export const useCourse = (id: string) => {
  return useQuery({
    queryKey: ['courses', id],
    queryFn: async (): Promise<Course> => {
      return await api(`/courses/${id}`);
    },
    enabled: !!id,
  });
};

export const useCreateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Course>): Promise<Course> => {
      return await api('/courses', {
        method: 'POST',
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

export const useUpdateCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Course> }): Promise<Course> => {
      return await api(`/courses/${id}`, {
        method: 'PUT',
        body: data,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      queryClient.invalidateQueries({ queryKey: ['courses', variables.id] });
    },
  });
};

export const useDeleteCourse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      return await api(`/courses/${id}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

export const useImportScorm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File): Promise<Course> => {
      const formData = new FormData();
      formData.append('file', file);
      return await apiFormData('/courses/import-scorm', {
        method: 'POST',
        body: formData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
};

// Файл модуля лежит в закрытом бакете: ссылкой его не открыть, скачиваем с токеном.
export async function downloadModuleFile(courseId: string, moduleId: string, fileName?: string) {
  const response = await fetch(`${API_URL}/courses/${courseId}/modules/${moduleId}/file`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Module file download failed');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(await response.blob());
  a.download = fileName || 'file';
  a.click();
  URL.revokeObjectURL(a.href);
}
